

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Mail, Send, MapPin, Phone, Clock } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import mainlogo from "@/assets/mylistiq-logo-new.png";
import CallToActionFooter from "@/components/Footer";
import { useState } from "react";
// import CallingBot from "./Callingbot";
// import Chatbot from "./Chatbot";
import BotLauncher from "@/components/BotLauncher";
import { endpoints } from "@/config/apiEndpoints";

const contactFormSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(100),
  lastName: z.string().trim().min(1, "Last name is required").max(100),
  email: z.string().trim().email("Invalid email address").max(255),
  message: z.string().trim().min(1, "Message is required").max(1000),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

const Contact = () => {
  const { toast } = useToast();
  const location = useLocation();
  const isContactPage = location.pathname === "/contact";
  const isLoggedIn = !!localStorage.getItem("access_token");

  const [loading, setLoading] = useState(false);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      message: "",
    },
  });

  const onSubmit = async (data: ContactFormValues) => {
    setLoading(true);

    try {
      const response = await fetch(
        endpoints.contact,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            first_name: data.firstName,
            last_name: data.lastName,
            email: data.email,
            message: data.message,
          }),
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        toast({
          title: "Message Sent! 🎉",
          description:
            result.message ||
            "Thank you for contacting us. We'll get back to you soon.",
            duration:1000
        });
        form.reset();
      } else {
        toast({
          title: "Oops!",
          description:
            result.message || "Failed to send message. Please try again.",
          variant: "destructive",
          duration:1000
        });
      }
    } catch (err) {
      toast({
        title: "Network Error",
        description:
          "Unable to connect. Please check your internet connection.",
        variant: "destructive",
        duration:1000
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
            <header className="sticky top-0 z-50 backdrop-blur-md bg-gradient-to-b  from-blue-100/90 to-blue-50/90">
        {/* Gradient bottom line */}
        <div className="h-[1.5px] w-full bg-gradient-to-r from-transparent to-transparent" />

        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <div className="flex items-center">
              <Link
                to="/"
                onClick={(e) => {
                  if (window.location.pathname === "/") {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }}
                className="flex items-center gap-2"
              >
                <img
                  src={mainlogo}
                  alt="MyListIQ"
                  className="h-12 w-auto drop-shadow-sm"
                />
              </Link>
            </div>

            {/* Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <Link
                to="/"
                className="text-slate-600 hover:text-blue-600 font-medium transition-colors"
              >
                Features
              </Link>

              <Link
                to="/"
                className="text-slate-600 hover:text-blue-600 font-medium transition-colors"
              >
                Pricing
              </Link>

              <Link
                to="/about"
                className="text-slate-600 hover:text-blue-600 font-medium transition-colors"
              >
                About
              </Link>

              {/* CTA Button */}
              <Button
                asChild
                size="sm"
                className="
            text-base font-semibold
            bg-gradient-to-r from-blue-600 to-indigo-600
            hover:from-blue-700 hover:to-indigo-700
            shadow-md hover:shadow-xl
            transition-all
          "
              >
                <Link
                  to={
                    localStorage.getItem("access_token")
                      ? "/dashboard"
                      : "/signup"
                  }
                >
                  {localStorage.getItem("access_token")
                    ? "Dashboard"
                    : "Sign Up"}
                </Link>
              </Button>
{/* 
              <Button
                asChild
                size="sm"
                className="
 text-base font-semibold
                bg-red-600
                hover:bg-red-700
                text-white
                shadow-md hover:shadow-xl
                transition-all duration-300
  "
              >
                <Link
                  to="/beta-agreement"
                  className="flex items-center justify-center px-4 py-2"
                >
                  Beta Agreement
                </Link>
              </Button> */}

              {/* <Button
                asChild
                size="sm"
                className="
 text-base font-semibold
                bg-red-600
                hover:bg-red-700
                text-white
                shadow-md hover:shadow-xl
                transition-all duration-300
"
              >
                <Link
                  to="/beta-checklist"
                  className="text-white-600 hover:text-white-600 font-medium transition-colors"
                >
                  Beta Checklist
                </Link>
              </Button> */}
            </div>
          </div>
        </div>
      </header>
      
      {/* <header className="border-b bg-background/95 backdrop-blur-md sticky top-0 z-50">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <Link to="/" className="flex items-center">
              <img src={mainlogo} alt="MyListIQ" className="h-12 w-auto" />
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              <a
                href="/#features"
                className="text-muted-foreground hover:text-primary transition-colors font-medium"
              >
                Features
              </a>
              <a
                href="/#pricing"
                className="text-muted-foreground hover:text-primary transition-colors font-medium"
              >
                Pricing
              </a>
              <a
                href="/about"
                className="text-muted-foreground hover:text-primary transition-colors font-medium"
              >
                About
              </a>
              {!isContactPage && (
                <Link
                  to="/contact"
                  className="text-primary font-semibold hover:text-primary/80 transition-colors"
                >
                  Contact
                </Link>
              )}
            </nav>

            <Button asChild variant="cta" size="sm">
              <Link to={isLoggedIn ? "/dashboard" : "/login"}>
                {isLoggedIn ? "Dashboard" : "Sign In to Dashboard"}
              </Link>
            </Button>
          </div>
        </div>
      </header> */}

<section className="px-6 py-10 lg:py-14">
  <div className="container mx-auto max-w-6xl">

    {/* PAGE HEADING */}
    <div className="text-center mb-12">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4 animate-pulse">
        <Mail className="w-8 h-8 text-primary" />
      </div>

      <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-3 text-foreground">
        Get in Touch
      </h1>

      <p className="text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
        Have questions about MyListIQ? We're here to help. Send us a message and
        we'll respond as soon as possible.
      </p>
    </div>

    {/* MAIN GRID — SIDE BY SIDE */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">

      {/* CONTACT FORM */}
      <div className="h-full flex flex-col bg-card/80 backdrop-blur-xl border border-border/50 rounded-2xl shadow-lg p-7 transition-all duration-500">
        <h2 className="text-2xl font-bold mb-6 text-foreground">
          Send us a Message
        </h2>

        <div className="flex-1 flex flex-col">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col flex-1 space-y-5"
            >

              {/* NAME */}
              <div className="grid sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">
                        First Name
                      </FormLabel>
                      <FormControl>
                        <Input
                          className="h-10 text-sm"
                          placeholder="Enter first name..."
                          disabled={loading}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">
                        Last Name
                      </FormLabel>
                      <FormControl>
                        <Input
                          className="h-10 text-sm"
                          placeholder="Enter last name..."
                          disabled={loading}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* EMAIL */}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      Email
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        className="h-10 text-sm"
                        placeholder="Enter email..."
                        disabled={loading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* MESSAGE */}
              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem className="flex flex-col flex-1">
                    <FormLabel className="text-sm font-medium">
                      Message
                    </FormLabel>
                    <FormControl className="flex-1">
                      <Textarea
                        className="min-h-[140px] h-full resize-none text-sm"
                        placeholder="Tell us how we can help you..."
                        disabled={loading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* SUBMIT */}
              <Button
                type="submit"
                size="lg"
                disabled={loading}
                className="w-full h-11 text-base font-semibold shadow-lg mt-auto"
              >
                {loading ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </Form>
        </div>
      </div>

      {/* CONTACT INFO */}
      <div className="h-full flex flex-col bg-card/80 backdrop-blur-xl border border-border/50 rounded-2xl shadow-lg p-7 transition-all duration-500">
        <h2 className="text-2xl font-bold mb-7 text-foreground">
          Contact Information
        </h2>

        <div className="space-y-7 flex-1">
          {[
            {
              icon: MapPin,
              title: "Our Location",
              desc: "5366 Central Florida Pkwy #1015 Orlando, FL 32821",
            },
            {
              icon: Mail,
              title: "Email Us",
              desc: "support@mylistiq.com",
            },
            {
              icon: Phone,
              title: "Call Us",
              desc: "Support: +1 (321) 519-4247",
            },
            {
              icon: Clock,
              title: "Business Hours",
              desc:
                "Monday - Friday: 9:00 AM - 6:00 PM\n" +
                "Saturday: 10:00 AM - 4:00 PM\n" +
                "Sunday: Closed",
            },
          ].map((item, idx) => (
            <div key={idx} className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <item.icon className="w-5 h-5 text-primary" />
              </div>

              <div>
                <h3 className="font-semibold text-base mb-1">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-sm whitespace-pre-line leading-relaxed">
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  </div>
</section>

{/* <CallingBot/>
<Chatbot/> */}
<BotLauncher />
      <CallToActionFooter /> 
    </div>
  );
};

export default Contact;
